import { openDB, deleteDB } from "idb";
import { Product, User } from "src/shared/types";
import { IUser } from "src/shared/services/interface";

const DB_NAME = "POS_GROCERY";
const DB_VERSION = 3;
const PRODUCT_STORE_NAME = "products";
const USER_DB_NAME = "iMetanicPOS";
const USER_DB_VERSION = 3;
const USER_STORE_NAME = "UserDetail";
const USER_KEY_PATH = "_id.$oid";
const CUSTOMER_CREDIT_CONFIG_STORE = "CustomerCreditConfig";

type StoredUser = Record<string, unknown>;
type UserToStore = IUser | User | StoredUser;

/** Create / Open DB */
export const dbPromise = openDB(DB_NAME, DB_VERSION, {
  upgrade(db) {
    if (!db.objectStoreNames.contains(PRODUCT_STORE_NAME)) {
      const store = db.createObjectStore(PRODUCT_STORE_NAME, {
        keyPath: "_id",
      });

      // Index for barcode search
      store.createIndex("barcode", "barcodeId", { unique: false });
    }
  },
});

/** Database used exclusively for the authenticated user's details. */
const userDbPromise = openDB(USER_DB_NAME, USER_DB_VERSION, {
  upgrade(db, _oldVersion, _newVersion, transaction) {
    const existingStore = db.objectStoreNames.contains(USER_STORE_NAME) ? transaction.objectStore(USER_STORE_NAME) : undefined;

    // IndexedDB cannot change a store key path in place. User details are a
    // cache, so recreate legacy stores to key records by Mongo ObjectId.
    if (existingStore && existingStore.keyPath !== USER_KEY_PATH) {
      db.deleteObjectStore(USER_STORE_NAME);
    }

    const userStore = db.objectStoreNames.contains(USER_STORE_NAME)
      ? transaction.objectStore(USER_STORE_NAME)
      : db.createObjectStore(USER_STORE_NAME, { keyPath: USER_KEY_PATH });

    if (!userStore.indexNames.contains("name")) {
      userStore.createIndex("name", "name", { unique: false });
    }
  },
});

const getStoredUserId = (user: UserToStore): string | undefined => {
  const mongoId = (user as any)._id;

  if (mongoId?.$oid) return String(mongoId.$oid);
  if (typeof mongoId === "string") return mongoId;
  if ((user as any).id) return String((user as any).id);
  if ((user as any).user_id) return String((user as any).user_id);

  return undefined;
};

/** Save users under their own user ID in iMetanicPOS/UserDetail. */
export const saveUserToIndexedDB = async (users: UserToStore | UserToStore[] | null | undefined) => {
  if (!users) {
    console.warn("Cannot save empty user data to IndexedDB");
    return;
  }

  const db = await userDbPromise;
  const tx = db.transaction(USER_STORE_NAME, "readwrite");
  const store = tx.objectStore(USER_STORE_NAME);
  const userList = Array.isArray(users) ? users : [users];

  for (const user of userList) {
    const id = getStoredUserId(user);
    if (!id) {
      console.warn("Skipping user without an ID:", user);
      continue;
    }

    const name = String((user as any).name || (user as any).firstName || (user as any).userName || "");
    await store.put({ ...user, _id: { $oid: id }, name });
  }

  await tx.done;
};

/** Get user details by their user ID. */
export const getUserFromIndexedDB = async (userId: string): Promise<StoredUser | undefined> => {
  const db = await userDbPromise;
  const user = await db.get(USER_STORE_NAME, userId);

  if (!user) return undefined;
  return user as StoredUser;
};

/** Get all users saved in iMetanicPOS/UserDetail. */
export const getUsersFromIndexedDB = async (): Promise<StoredUser[]> => {
  const db = await userDbPromise;
  return db.getAll(USER_STORE_NAME) as Promise<StoredUser[]>;
};

/** Delete one user record from iMetanicPOS/UserDetail by Mongo ObjectId. */
export const deleteUserFromIndexedDB = async (userId: string) => {
  const db = await userDbPromise;
  await db.delete(USER_STORE_NAME, userId);
};

/** Clear all cached records from iMetanicPOS/UserDetail. */
export const clearUserDetailsFromIndexedDB = async () => {
  const db = await userDbPromise;
  await db.clear(USER_STORE_NAME);
};

/** ✅ Save Products (Safe) */
export const saveProductsToIndexedDB = async (products: Product[] | null | undefined) => {
  if (!Array.isArray(products)) {
    console.warn("❌ Products is not an array:", products);
    return;
  }

  const db = await dbPromise;
  const tx = db.transaction(PRODUCT_STORE_NAME, "readwrite");
  const store = tx.objectStore(PRODUCT_STORE_NAME);

  for (const product of products) {
    let id: any = product?._id;

    // Handle Mongo ObjectId { $oid: "..." }
    if (typeof id === "object" && id?.$oid) {
      id = id.$oid;
    }

    // Convert to string
    if (id) {
      id = String(id);
    }

    // Skip invalid
    if (!id) {
      console.warn("⚠️ Skipping invalid product:", product);
      continue;
    }

    const formattedProduct = {
      ...product,
      _id: id,
    };

    await store.put(formattedProduct);
  }

  await tx.done;
};

/** ✅ Get All Products */
export const getProductsFromIndexedDB = async (): Promise<Product[]> => {
  const db = await dbPromise;
  return db.getAll(PRODUCT_STORE_NAME);
};

/** ✅ Get Product by Barcode */
export const getProductByBarcode = async (barcode: string) => {
  const db = await dbPromise;
  const tx = db.transaction(PRODUCT_STORE_NAME, "readonly");
  const index = tx.objectStore(PRODUCT_STORE_NAME).index("barcode");

  const result = await index.get(barcode);
  return result;
};

/** ✅ Delete Single Product */
export const deleteProductFromIndexedDB = async (id: string) => {
  const db = await dbPromise;
  const tx = db.transaction(PRODUCT_STORE_NAME, "readwrite");
  const store = tx.objectStore(PRODUCT_STORE_NAME);

  await store.delete(id);

  await tx.done;
};

/** ✅ Clear All Products (Recommended reset) */
export const clearProductsFromIndexedDB = async () => {
  const db = await dbPromise;
  const tx = db.transaction(PRODUCT_STORE_NAME, "readwrite");

  await tx.objectStore(PRODUCT_STORE_NAME).clear();

  await tx.done;
};

/** ❌ Delete Entire DB (Hard Reset) */
export const deleteIndexedDB = async () => {
  await deleteDB(DB_NAME);
};

/** 🚀 Reset + Save (Best Practice) */
export const resetAndSaveProducts = async (products: Product[] | null | undefined) => {
  await clearProductsFromIndexedDB();
  await saveProductsToIndexedDB(products);
};
